/**
 * KuendigungAssistant.jsx — iter167
 *
 * Wizard für Vermieterkündigungen mit BGB-Textbausteinen. 3 Modi:
 *   1) Eigenbedarf              (§ 573 Abs. 2 Nr. 2 BGB, § 573c Frist)
 *   2) Vertragsverletzung        (§ 573 Abs. 2 Nr. 1 BGB, § 573c Frist)
 *   3) Zahlungsrückstand fristlos (§ 543 Abs. 2 Nr. 3 BGB)
 *
 * Der Wizard berechnet die früheste rechtmäßige Kündigungswirkung, füllt die
 * Zusatzfelder vor und erzeugt anschließend das Kündigungsschreiben über die
 * bestehende Amtliche-Doku-Pipeline (mit editierbarem Anschreiben).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CaretRight, CaretLeft, Eye, PaperPlaneTilt, Warning } from "@phosphor-icons/react";

const MODES = [
  {
    id: "eigenbedarf",
    template_id: "kuendigung_eigenbedarf",
    title: "Eigenbedarf",
    bgb: "§ 573 Abs. 2 Nr. 2 BGB",
    frist_art: "eigenbedarf",
    color: "bg-amber-50 border-amber-200 text-amber-900",
    short: "Kündigung, weil die Wohnung für Sie selbst oder nahe Angehörige benötigt wird.",
  },
  {
    id: "vertragsverletzung",
    template_id: "kuendigung_vertragsverletzung",
    title: "Vertragsverletzung",
    bgb: "§ 573 Abs. 2 Nr. 1 BGB",
    frist_art: "vertragsverletzung",
    color: "bg-orange-50 border-orange-200 text-orange-900",
    short: "Ordentliche Kündigung nach schuldhafter, nicht unerheblicher Vertragsverletzung (Abmahnung erforderlich).",
  },
  {
    id: "zahlungsrueckstand",
    template_id: "kuendigung_zahlungsrueckstand",
    title: "Zahlungsrückstand (fristlos)",
    bgb: "§ 543 Abs. 2 Nr. 3 BGB",
    frist_art: "zahlungsrueckstand",
    color: "bg-rose-50 border-rose-200 text-rose-900",
    short: "Außerordentliche Kündigung ohne Frist wegen erheblichen Zahlungsverzugs (2 Monatsmieten).",
  },
];

export default function KuendigungAssistant({ open, onClose, tenancyId, onCreated }) {
  const [step, setStep] = useState(0); // 0=Auswahl, 1=Details, 2=Anschreiben, 3=Versand
  const [mode, setMode] = useState(null);
  const [fristInfo, setFristInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState({
    termin: "",
    grund: "",
    eigenbedarf_person: "",
    eigenbedarf_verwandt: "",
    abmahnung_datum: "",
    rueckstand_eur: "",
    rueckstand_periode: "",
    include_widerspruch: true,  // iter168 · § 574 BGB Widerspruchsrecht-Hinweis
  });
  const [coverBody, setCoverBody] = useState("");
  const [coverEdited, setCoverEdited] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(0); setMode(null); setFristInfo(null);
      setDetails({ termin: "", grund: "", eigenbedarf_person: "", eigenbedarf_verwandt: "",
                   abmahnung_datum: "", rueckstand_eur: "", rueckstand_periode: "",
                   include_widerspruch: true });
      setCoverBody(""); setCoverEdited(false);
    }
  }, [open]);

  const selectMode = async (m) => {
    setMode(m);
    setBusy(true);
    try {
      const { data } = await api.post("/official-documents/kuendigung-frist", {
        tenancy_id: tenancyId, art: m.frist_art,
      });
      setFristInfo(data);
      setDetails((d) => ({ ...d, termin: data.earliest_termination_de || "" }));
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Frist-Berechnung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const loadCover = async () => {
    if (!mode) return;
    setBusy(true);
    try {
      const { data } = await api.post("/official-documents/cover-text", {
        template_id: mode.template_id, tenancy_id: tenancyId,
        extra_context: { kuendigung: details },
      });
      setCoverBody(data?.text || "");
    } catch (e) { toast.error("Anschreiben konnte nicht geladen werden"); }
    finally { setBusy(false); }
  };

  const previewCover = async () => {
    if (!mode) return;
    setBusy(true);
    try {
      const res = await api.post("/official-documents/preview", {
        template_id: mode.template_id, tenancy_id: tenancyId,
        extra_context: { kuendigung: details },
        render_cover_only: true,
        cover_body_override: coverEdited && coverBody ? coverBody : null,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 6000);
    } catch (e) { toast.error("Vorschau fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const previewFull = async () => {
    if (!mode) return;
    setBusy(true);
    try {
      const res = await api.post("/official-documents/preview", {
        template_id: mode.template_id, tenancy_id: tenancyId,
        extra_context: { kuendigung: details },
        cover_body_override: coverEdited && coverBody ? coverBody : null,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 6000);
    } catch (e) { toast.error("Vorschau fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const finalize = async (method) => {
    if (!mode) return;
    setBusy(true);
    try {
      if (method === "download") {
        const res = await api.post("/official-documents/generate", {
          template_id: mode.template_id, tenancy_id: tenancyId,
          subject: `Kündigung des Mietverhältnisses (${mode.title})`,
          include_cover: true, raw_form_only: false, save_to_docs: true,
          extra_context: { kuendigung: details },
          cover_body_override: coverEdited && coverBody ? coverBody : null,
        }, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${mode.template_id}.pdf`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 6000);
        toast.success("Kündigungsschreiben erzeugt & in Mappe abgelegt");
      } else {
        const res = await api.post("/official-documents/direct-send", {
          template_id: mode.template_id, tenancy_id: tenancyId,
          method,
          subject: `Kündigung des Mietverhältnisses (${mode.title})`,
          extra_context: { kuendigung: details },
          cover_body_override: coverEdited && coverBody ? coverBody : null,
        }, { responseType: method === "post" ? "blob" : "json" });
        if (method === "post") {
          const url = URL.createObjectURL(res.data);
          const win = window.open(url, "_blank");
          setTimeout(() => { win?.focus(); win?.print(); }, 900);
          toast.success("Druckansicht geöffnet · in Mappe abgelegt");
        } else if (method === "email") {
          toast.success(`E-Mail versendet: ${res.data?.message_id || "OK"}`);
        }
      }
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erzeugung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const canProceed = useMemo(() => {
    if (step === 1) {
      if (!details.termin.trim()) return false;
      if (mode?.id === "eigenbedarf" && !details.eigenbedarf_person.trim()) return false;
      if (mode?.id === "vertragsverletzung" && !details.abmahnung_datum) return false;
      if (mode?.id === "zahlungsrueckstand"
          && (!details.rueckstand_eur.trim() || !details.rueckstand_periode.trim())) return false;
      return true;
    }
    return true;
  }, [step, details, mode]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="kuendigung-assistant">
        <DialogHeader>
          <DialogTitle>Kündigungs-Assistent</DialogTitle>
          <DialogDescription>
            Rechtssichere Vermieterkündigung mit BGB-Textbausteinen und automatischer Fristberechnung.
          </DialogDescription>
        </DialogHeader>

        {/* Schritt-Anzeige */}
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          {["Grund wählen", "Details prüfen", "Anschreiben", "Versand"].map((label, i) => (
            <div key={i} className={`flex items-center gap-1 ${i === step ? "font-semibold text-teal-700" : ""}`}>
              <span className={`inline-block w-5 h-5 rounded-full text-center leading-5 text-[10px] ${i === step ? "bg-teal-600 text-white" : "bg-slate-200"}`}>{i + 1}</span>
              {label}
              {i < 3 && <CaretRight size={12} className="text-slate-400" />}
            </div>
          ))}
        </div>

        {/* Step 0: Auswahl */}
        {step === 0 && (
          <div className="space-y-2" data-testid="kuendigung-step-0">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => selectMode(m)}
                disabled={busy}
                className={`w-full text-left border-2 rounded-md p-4 hover:shadow-md transition ${m.color}`}
                data-testid={`kuendigung-mode-${m.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{m.title}</span>
                  <span className="text-[10px] uppercase tracking-wider">{m.bgb}</span>
                </div>
                <div className="text-xs mt-1 opacity-90">{m.short}</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 1: Details */}
        {step === 1 && mode && (
          <div className="space-y-4" data-testid="kuendigung-step-1">
            {fristInfo && (
              <div className="bg-teal-50 border border-teal-200 rounded-md p-3 text-xs text-teal-900">
                <div className="font-semibold mb-1">Berechnete Frist</div>
                <div>{fristInfo.rule_text}</div>
                <div className="mt-1">
                  Mietdauer: <b>{fristInfo.tenancy_years} Jahre</b> ·
                  Vertragsbeginn <b>{new Date(fristInfo.contract_start).toLocaleDateString("de-DE")}</b>
                </div>
                <div className="mt-1">
                  Frühestmögliches Vertragsende: <b>{fristInfo.earliest_termination_de}</b> ({fristInfo.months_notice} Monate Frist)
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Vertragsende (TT.MM.JJJJ)</Label>
              <Input value={details.termin} onChange={(e) => setDetails({ ...details, termin: e.target.value })}
                placeholder="z. B. 31.12.2026" data-testid="k-termin" />
              <div className="text-[11px] text-slate-500 mt-1">
                Vorbelegt mit dem frühestmöglichen Termin — Sie können später kündigen, aber nicht früher.
              </div>
            </div>

            {mode.id === "eigenbedarf" && (
              <>
                <div>
                  <Label className="text-xs">Bedarfsperson (Name)</Label>
                  <Input value={details.eigenbedarf_person} onChange={(e) => setDetails({ ...details, eigenbedarf_person: e.target.value })}
                    placeholder="z. B. Anna Müller" data-testid="k-eb-person" />
                </div>
                <div>
                  <Label className="text-xs">Verwandtschaftsgrad</Label>
                  <Input value={details.eigenbedarf_verwandt} onChange={(e) => setDetails({ ...details, eigenbedarf_verwandt: e.target.value })}
                    placeholder="z. B. Tochter des Vermieters" data-testid="k-eb-relation" />
                </div>
              </>
            )}
            {mode.id === "vertragsverletzung" && (
              <div>
                <Label className="text-xs">Datum der Abmahnung</Label>
                <Input type="date" value={details.abmahnung_datum} onChange={(e) => setDetails({ ...details, abmahnung_datum: e.target.value })}
                  data-testid="k-abmahnung" />
                <div className="text-[11px] text-slate-500 mt-1">
                  Eine wirksame Abmahnung ist Voraussetzung — bitte Zeitpunkt angeben.
                </div>
              </div>
            )}
            {mode.id === "zahlungsrueckstand" && (
              <>
                <div>
                  <Label className="text-xs">Rückstand (EUR)</Label>
                  <Input value={details.rueckstand_eur} onChange={(e) => setDetails({ ...details, rueckstand_eur: e.target.value })}
                    placeholder="z. B. 1.850,00 €" data-testid="k-rueckstand-eur" />
                </div>
                <div>
                  <Label className="text-xs">Zeitraum</Label>
                  <Input value={details.rueckstand_periode} onChange={(e) => setDetails({ ...details, rueckstand_periode: e.target.value })}
                    placeholder="z. B. Januar und Februar 2026" data-testid="k-rueckstand-periode" />
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-[11px] text-rose-900 flex items-start gap-2">
                  <Warning size={14} className="mt-0.5 shrink-0" />
                  <span>
                    § 543 Abs. 2 Nr. 3 BGB setzt voraus: Rückstand von mindestens einer
                    Monatsmiete an <b>zwei aufeinanderfolgenden Terminen</b> ODER Gesamtrückstand
                    von mehr als <b>zwei Monatsmieten</b>. Innerhalb 2 Monaten heilbar (§ 569 III BGB).
                  </span>
                </div>
              </>
            )}
            <div>
              <Label className="text-xs">Begründung / Sachverhalt (Freitext)</Label>
              <textarea
                className="mt-1 w-full min-h-[100px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={details.grund}
                onChange={(e) => setDetails({ ...details, grund: e.target.value })}
                placeholder="Konkrete Begründung, Sachverhalt, weitere Details"
                data-testid="k-grund"
              />
            </div>
            {/* iter168 · Widerspruchsrecht-Toggle */}
            <label className="flex items-start gap-2 text-xs cursor-pointer bg-slate-50 border border-slate-200 rounded-md p-2">
              <input
                type="checkbox"
                checked={details.include_widerspruch}
                onChange={(e) => setDetails({ ...details, include_widerspruch: e.target.checked })}
                className="mt-0.5"
                data-testid="k-widerspruch"
              />
              <span>
                <b>Widerspruchsrecht-Hinweis nach §§ 574, 574a BGB aufnehmen</b> (empfohlen).
                Belehrt den Mieter über sein Widerspruchsrecht bei sozialer Härte — Voraussetzung dafür, dass
                Widerspruch <em>vor</em> Vertragsende erhoben werden muss.
              </span>
            </label>
          </div>
        )}

        {/* Step 2: Anschreiben */}
        {step === 2 && mode && (
          <div className="space-y-3" data-testid="kuendigung-step-2">
            <div className="text-xs text-slate-600">
              Das Anschreiben wird auf Ihrem Geschäftspapier ausgegeben und dem Kündigungsschreiben
              vorangestellt. Sie können den Text hier anpassen — Absätze durch Leerzeile trennen.
            </div>
            {!coverBody && !busy && (
              <Button variant="outline" size="sm" onClick={loadCover} data-testid="kuendigung-load-cover">
                Standard-Anschreiben laden
              </Button>
            )}
            {busy && !coverBody && <div className="text-sm text-slate-500 py-4">Lade Anschreiben…</div>}
            {coverBody && (
              <textarea
                className="w-full min-h-[260px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono leading-6"
                value={coverBody}
                onChange={(e) => { setCoverBody(e.target.value); setCoverEdited(true); }}
                data-testid="kuendigung-cover-textarea"
              />
            )}
            {coverBody && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={previewCover} disabled={busy}
                        data-testid="kuendigung-cover-preview">
                  <Eye size={12} className="mr-1.5" /> Anschreiben-Vorschau
                </Button>
                <Button variant="outline" size="sm" onClick={previewFull} disabled={busy}
                        data-testid="kuendigung-full-preview">
                  <Eye size={12} className="mr-1.5" /> Gesamt-Vorschau (Anschreiben + Kündigung)
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Versand */}
        {step === 3 && mode && (
          <div className="space-y-3" data-testid="kuendigung-step-3">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm space-y-1">
              <div><b>Kündigungsart:</b> {mode.title} ({mode.bgb})</div>
              <div><b>Wirksam zum:</b> {details.termin}</div>
              {mode.id === "eigenbedarf" && (
                <div><b>Bedarfsperson:</b> {details.eigenbedarf_person} · {details.eigenbedarf_verwandt}</div>
              )}
              {mode.id === "zahlungsrueckstand" && (
                <div><b>Rückstand:</b> {details.rueckstand_eur} ({details.rueckstand_periode})</div>
              )}
              <div className="text-[11px] text-slate-500 mt-2">
                Bitte prüfen. Nach Auslösung wird das Dokument in der Dokumentenmappe abgelegt.
              </div>
            </div>
            <div className="grid gap-2">
              <Button variant="outline" className="justify-start h-auto py-3" onClick={() => finalize("post")} disabled={busy} data-testid="kuendigung-send-post">
                <PaperPlaneTilt size={16} className="text-teal-600 mr-2" />
                <div className="text-left">
                  <div className="font-semibold text-sm">Per Post · Druckansicht öffnen</div>
                  <div className="text-xs text-slate-500">Anschreiben + Kündigung als kombinierte PDF</div>
                </div>
              </Button>
              <Button variant="outline" className="justify-start h-auto py-3" onClick={() => finalize("email")} disabled={busy} data-testid="kuendigung-send-email">
                <PaperPlaneTilt size={16} className="text-teal-600 mr-2" />
                <div className="text-left">
                  <div className="font-semibold text-sm">Per E-Mail versenden</div>
                  <div className="text-xs text-slate-500">Anschreiben als Body · Kündigung als PDF-Anhang</div>
                </div>
              </Button>
              <Button variant="ghost" onClick={() => finalize("download")} disabled={busy} data-testid="kuendigung-download">
                Nur PDF herunterladen (kein Versand)
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap justify-between gap-2">
          <div>
            {step > 0 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={busy}
                      data-testid="kuendigung-back">
                <CaretLeft size={14} className="mr-1" /> Zurück
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
            {step > 0 && step < 3 && (
              <Button className="bg-teal-600 hover:bg-teal-700 text-white"
                      onClick={() => setStep(step + 1)} disabled={busy || !canProceed}
                      data-testid="kuendigung-next">
                Weiter <CaretRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
